/**
 * Product analytics — single typed funnel for every client event.
 *
 * Pattern, intentional:
 *   1. `EventSchemas` is the canonical catalog. Adding a new event = add one
 *      line here, then call `track('<name>', { ...typed props })`. TypeScript
 *      enforces the prop shape at every call site — no stringly-typed events.
 *   2. Everything goes through `track()`. Nothing calls `sendBeacon` directly.
 *   3. Page views fire automatically at the router level (see `App.tsx`), so
 *      any new route is tracked from day one — no per-feature instrumentation
 *      required just to see "did anyone visit this?".
 *
 * Transport: events are batched in memory and flushed to `POST /api/events`
 * on (a) queue size ≥ 20, (b) every 15 s, (c) `pagehide` / `visibilitychange→
 * hidden` via `navigator.sendBeacon` (the only API that survives tab close).
 *
 * Privacy: no PII is sent. `uid` is the user's Cognito sub (an opaque id) and
 * is only attached when the user is signed in. Kill switch via the env var
 * `REACT_APP_ANALYTICS=false` — disables all tracking for local dev or for a
 * future user-facing opt-out toggle.
 */

import { API_URL } from '../auth/cognito';

// ── Event catalog (single source of truth) ───────────────────────────────────
// To add a new event: add a line here, then call `track('your_event', { ... })`
// at the point in code where the user-meaningful action happens.

export interface EventSchemas {
  session_start:  Record<string, never>;
  page_view:      { path: string };
  problem_open:   { problem: string; skill: string; tier: number; difficulty: string };
  problem_run:    { problem: string; lang: string; result: 'pass' | 'fail'; passed: number; total: number };
  problem_close:  { problem: string; dwell_ms: number; runs: number; solved: boolean };
  hint_reveal:    { problem: string; index: number };
}

type EventName = keyof EventSchemas;

// ── Configuration ────────────────────────────────────────────────────────────

const ENABLED = process.env.REACT_APP_ANALYTICS !== 'false';
const BATCH_FLUSH_THRESHOLD = 20;
const FLUSH_INTERVAL_MS = 15_000;
const ENDPOINT = `${API_URL.replace(/\/+$/, '')}/api/events`;

// ── Session id + user id ─────────────────────────────────────────────────────
// `sid` is per-tab (sessionStorage); `uid` is set once auth resolves. Both are
// attached to every outgoing event by the funnel so any query can group by
// user or session without joining log lines.

const SID_STORAGE_KEY = 'sb.analytics.sid';

function makeId(): string {
  // crypto.randomUUID is available in all modern browsers + the test env when
  // jsdom is recent enough; fall back to a non-cryptographic id otherwise.
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  } catch { /* fallthrough */ }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function getSid(): string {
  try {
    let sid = sessionStorage.getItem(SID_STORAGE_KEY);
    if (!sid) {
      sid = makeId();
      sessionStorage.setItem(SID_STORAGE_KEY, sid);
    }
    return sid;
  } catch {
    // sessionStorage disabled (Safari private mode pre-iOS17, etc.) — fall
    // back to an in-memory id so the session is at least internally coherent.
    return _inMemorySid;
  }
}
const _inMemorySid = makeId();

let _uid: string | null = null;

/** Called from the auth shell once the signed-in user is resolved. */
export function setAnalyticsUid(uid: string | null): void {
  _uid = uid && uid.length <= 64 ? uid : null;
}

// ── Queue + flush ────────────────────────────────────────────────────────────

interface QueuedEvent {
  e: string;
  t: number;
  p: Record<string, unknown>;
}

let _queue: QueuedEvent[] = [];
let _timer: ReturnType<typeof setInterval> | null = null;

function ensureTimer(): void {
  if (_timer !== null || typeof window === 'undefined') return;
  _timer = setInterval(() => {
    if (_queue.length > 0) flush();
  }, FLUSH_INTERVAL_MS);
}

/**
 * Record an event. Type-safe against `EventSchemas`. Never throws — analytics
 * must never break the user's session.
 */
export function track<E extends EventName>(event: E, props: EventSchemas[E]): void {
  if (!ENABLED) return;
  try {
    _queue.push({
      e: event,
      t: Date.now(),
      p: { ...(props as Record<string, unknown>) },
    });
    ensureTimer();
    if (_queue.length >= BATCH_FLUSH_THRESHOLD) flush();
  } catch {
    // Last-ditch safety: a malformed prop dict (Symbols, circular refs) must
    // not bubble into the caller's render path. Drop the event instead.
  }
}

/**
 * Flush the in-memory queue. Uses `navigator.sendBeacon` when available
 * (survives `pagehide`); falls back to `fetch` with `keepalive: true`.
 * The endpoint accepts text/plain to keep the request a CORS "simple request"
 * — sendBeacon and CORS preflight don't get along.
 */
export function flush(): void {
  if (!ENABLED || _queue.length === 0) return;
  const batch = _queue;
  _queue = [];
  const payload = JSON.stringify({
    sid: getSid(),
    uid: _uid ?? undefined,
    events: batch,
  });
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([payload], { type: 'text/plain;charset=UTF-8' });
      const ok = navigator.sendBeacon(ENDPOINT, blob);
      if (ok) return;
      // sendBeacon returns false when the body is too large or the browser
      // refused to queue it — fall through to fetch so we don't drop silently.
    }
    void fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body: payload,
      keepalive: true,
    }).catch(() => { /* analytics is never load-bearing */ });
  } catch {
    // Re-queueing failed sends would loop forever on a permanently broken
    // network. Drop and move on; the next event triggers another flush.
  }
}

// ── Lifecycle hooks (idempotent install) ─────────────────────────────────────

let _installed = false;

/** Wire up flush-on-hide listeners. Safe to call multiple times. */
export function installAnalytics(): void {
  if (_installed || !ENABLED || typeof window === 'undefined') return;
  _installed = true;
  // `pagehide` is the only event that fires reliably across desktop + mobile
  // (incl. iOS Safari back-forward cache). `visibilitychange→hidden` covers
  // the case where the user switches tabs without closing.
  const flushOnHide = () => flush();
  window.addEventListener('pagehide', flushOnHide);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushOnHide();
  });
  ensureTimer();
}
